import React from "react";
import {
  Menu,
  Shield,
  LogOut,
  Clock,
  Download,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { UserProfile } from "../../types/userProfile";
import { GameState, AppTheme } from "../../types";
import { ThemeSelector } from "./ThemeSelector";
import { NotificationCenter } from "../notifications/NotificationCenter";

interface AppHeaderProps {
  activeTab: string;
  setActiveTab: (tab: any) => void;
  setIsSidebarOpen: (open: boolean) => void;
  adminSubTab: string;
  setAdminSubTab: (subTab: any) => void;
  userProfile: UserProfile;
  isAdminMode: boolean;
  gameState: GameState;
  isTimerRunning: boolean;
  matchSeconds: number;
  formatTime: (sec: number) => string;
  setShowDownloadAppModal: (show: boolean) => void;
  zoomLevel: number;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  handleResetZoom: () => void;
  theme: AppTheme;
  setTheme: React.Dispatch<React.SetStateAction<AppTheme>>;
}

export const AppHeader: React.FC<AppHeaderProps> = ({
  activeTab,
  setActiveTab,
  setIsSidebarOpen,
  adminSubTab,
  setAdminSubTab,
  userProfile,
  isAdminMode,
  gameState,
  isTimerRunning,
  matchSeconds,
  formatTime,
  setShowDownloadAppModal,
  zoomLevel,
  handleZoomIn,
  handleZoomOut,
  handleResetZoom,
  theme,
  setTheme,
}) => {
  return (
    <header className="border-b border-slate-200 dark:border-emerald-900/60 bg-white/95 dark:bg-[#041208]/95 backdrop-blur-md sticky top-0 z-20 px-4 sm:px-8 py-3.5 flex items-center justify-between transition-colors">
      {activeTab === "admin" ? (
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsSidebarOpen(true)}
              className="p-2 rounded-xl bg-amber-400/10 dark:bg-amber-400/20 border border-amber-400/40 text-amber-600 dark:text-amber-300 hover:bg-amber-400/20 transition cursor-pointer flex items-center justify-center"
              aria-label="Abrir menu admin"
            >
              <Menu size={20} />
            </button>
            <div>
              <span className="text-sm sm:text-base font-display font-black text-amber-600 dark:text-amber-300 tracking-wider flex items-center gap-2 select-none">
                <Shield size={18} className="text-amber-500 dark:text-amber-400" />
                PAINEL SUPER ADMIN
              </span>
              <p className="text-[10px] text-slate-500 dark:text-zinc-400 hidden sm:block font-bold">
                Central de Gestão & Configuração do Site
              </p>
            </div>
          </div>

          {/* ATALHOS RAPIDOS DE ABAS DO ADMIN NO DESKTOP */}
          <div className="hidden xl:flex items-center gap-1 bg-slate-100 dark:bg-zinc-900/80 p-1 rounded-2xl border border-slate-200 dark:border-zinc-800">
            <button
              type="button"
              onClick={() => setAdminSubTab("quadras")}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
                adminSubTab === "quadras"
                  ? "bg-amber-400 text-emerald-950 font-black shadow"
                  : "text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              Quadras
            </button>
            <button
              type="button"
              onClick={() => setAdminSubTab("fluxo_financeiro")}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
                adminSubTab === "fluxo_financeiro"
                  ? "bg-amber-400 text-emerald-950 font-black shadow"
                  : "text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              Financeiro
            </button>
            <button
              type="button"
              onClick={() => setAdminSubTab("bancos")}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
                adminSubTab === "bancos"
                  ? "bg-amber-400 text-emerald-950 font-black shadow"
                  : "text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              Bancos/Pix
            </button>
            <button
              type="button"
              onClick={() => setAdminSubTab("torneios")}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
                adminSubTab === "torneios"
                  ? "bg-amber-400 text-emerald-950 font-black shadow"
                  : "text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              Torneios
            </button>
            <button
              type="button"
              onClick={() => setAdminSubTab("configuracoes")}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
                adminSubTab === "configuracoes"
                  ? "bg-amber-400 text-emerald-950 font-black shadow"
                  : "text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              Configurações
            </button>
            <button
              type="button"
              onClick={() => setAdminSubTab("aprovacao_arenas")}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
                adminSubTab === "aprovacao_arenas"
                  ? "bg-amber-400 text-emerald-950 font-black shadow"
                  : "text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              Auditoria Arenas
            </button>
            <button
              type="button"
              onClick={() => setAdminSubTab("denuncias_arenas")}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
                adminSubTab === "denuncias_arenas"
                  ? "bg-amber-400 text-emerald-950 font-black shadow"
                  : "text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              Denúncias
            </button>
            <button
              type="button"
              onClick={() => setAdminSubTab("bloqueios")}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
                adminSubTab === "bloqueios"
                  ? "bg-amber-400 text-emerald-950 font-black shadow"
                  : "text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              Bloqueios
            </button>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => setActiveTab("hub")}
              className="bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs px-3.5 py-1.5 rounded-xl transition cursor-pointer shadow flex items-center gap-1.5"
            >
              <LogOut size={14} />
              <span>Sair do Admin</span>
            </button>

            {/* Ferramenta de Zoom (Escala da Tela) */}
            <div
              className={`hidden sm:flex items-center gap-1 p-1 rounded-xl border ${
                theme === "dark"
                  ? "bg-emerald-950/60 border-emerald-900/50 text-emerald-200"
                  : "bg-slate-100 border-slate-200 text-slate-700"
              }`}
            >
              <button
                type="button"
                onClick={handleZoomOut}
                disabled={zoomLevel <= 75}
                className="p-1 rounded-lg hover:bg-black/5 dark:hover:bg-emerald-800/40 disabled:opacity-30 transition cursor-pointer"
                title="Diminuir Zoom"
              >
                <ZoomOut size={15} />
              </button>
              <button
                type="button"
                onClick={handleResetZoom}
                className="text-[10px] font-mono font-extrabold px-1 py-0.5 hover:text-amber-500 dark:hover:text-amber-400 transition cursor-pointer select-none"
                title="Restaurar Zoom (100%)"
              >
                {zoomLevel}%
              </button>
              <button
                type="button"
                onClick={handleZoomIn}
                disabled={zoomLevel >= 140}
                className="p-1 rounded-lg hover:bg-black/5 dark:hover:bg-emerald-800/40 disabled:opacity-30 transition cursor-pointer"
                title="Aumentar Zoom"
              >
                <ZoomIn size={15} />
              </button>
            </div>

            {/* Central de Notificações */}
            <NotificationCenter
              userProfile={userProfile}
              onNavigate={(path) => {
                const targetTab = path.replace(/^\//, "");
                if (targetTab) setActiveTab(targetTab);
              }}
            />

            {/* Seletor de Tema (Claro / Escuro) */}
            <ThemeSelector
              currentTheme={theme}
              onThemeChange={setTheme}
              variant="compact"
            />
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsSidebarOpen(true)}
              className="p-2 rounded-xl bg-slate-100 dark:bg-emerald-950/60 border border-slate-200 dark:border-emerald-900/50 text-slate-700 dark:text-emerald-300 hover:text-slate-900 dark:hover:text-white transition cursor-pointer flex items-center justify-center"
              aria-label="Abrir menu"
            >
              <Menu size={20} />
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("hub")}
              className="flex items-center gap-2.5 select-none cursor-pointer hover:opacity-95 transition"
              title="Hub Esportivo"
            >
              <img
                src="/branding/hub-esportivo-mark.png"
                alt="Hub Esportivo"
                className="h-8 sm:h-9 w-auto object-contain shrink-0"
              />
              <span className="font-display font-black text-sm sm:text-base text-slate-900 dark:text-white tracking-wider">
                HUB ESPORTIVO
              </span>
            </button>

            {/* Quick Access Top Bar Tabs for Desktop */}
            <div className="hidden lg:flex items-center gap-1.5 ml-4 border-l border-slate-200 dark:border-emerald-900/60 pl-4">
              <button
                type="button"
                onClick={() => setActiveTab("hub")}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
                  activeTab === "hub"
                    ? "bg-amber-400 text-emerald-950 font-black shadow-sm"
                    : "text-slate-600 dark:text-emerald-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-emerald-950/60"
                }`}
              >
                Hub
              </button>
              {userProfile.accountType !== "dono_arena" && (
                <button
                  type="button"
                  onClick={() => setActiveTab("jogo")}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
                    activeTab === "jogo" || activeTab === "racha"
                      ? "bg-amber-400 text-emerald-950 font-black shadow-sm"
                      : "text-slate-600 dark:text-emerald-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-emerald-950/60"
                  }`}
                >
                  Sorteios e Jogos
                </button>
              )}
              <button
                type="button"
                onClick={() => setActiveTab("quadras")}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
                  activeTab === "quadras"
                    ? "bg-amber-400 text-emerald-950 font-black shadow-sm"
                    : "text-slate-600 dark:text-emerald-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-emerald-950/60"
                }`}
              >
                {userProfile.accountType === "dono_arena" ? "Minhas Quadras" : "Quadras"}
              </button>
              {userProfile.accountType === "dono_arena" && (
                <>
                  <button
                    type="button"
                    id="nav-header-vendas"
                    onClick={() => setActiveTab("vendas")}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
                      activeTab === "vendas"
                        ? "bg-amber-400 text-emerald-950 font-black shadow-sm"
                        : "text-slate-600 dark:text-emerald-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-emerald-950/60"
                    }`}
                  >
                    Vendas (PDV)
                  </button>
                  <button
                    type="button"
                    id="nav-header-horarios-eventos"
                    onClick={() => setActiveTab("dayuse")}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
                      activeTab === "dayuse" || activeTab === "mensalistas"
                        ? "bg-amber-400 text-emerald-950 font-black shadow-sm"
                        : "text-slate-600 dark:text-emerald-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-emerald-950/60"
                    }`}
                  >
                    Horários & Eventos
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("financeiro")}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
                      activeTab === "financeiro"
                        ? "bg-amber-400 text-emerald-950 font-black shadow-sm"
                        : "text-slate-600 dark:text-emerald-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-emerald-950/60"
                    }`}
                  >
                    Financeiro
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => setActiveTab("torneios")}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
                  activeTab === "torneios"
                    ? "bg-amber-400 text-emerald-950 font-black shadow-sm"
                    : "text-slate-600 dark:text-emerald-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-emerald-950/60"
                }`}
              >
                Torneios
              </button>
              {isAdminMode && (
                <button
                  type="button"
                  onClick={() => setActiveTab("admin")}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-1.5 ${
                    activeTab === "admin"
                      ? "bg-amber-400 text-emerald-950 font-black shadow-sm"
                      : "text-amber-600 dark:text-amber-300 hover:bg-amber-400/20 border border-amber-400/40"
                  }`}
                >
                  <Shield size={13} />
                  <span>Super Admin</span>
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {gameState.emAndamento && (
              <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-300 dark:border-emerald-800/80 px-2.5 sm:px-3.5 py-1.5 rounded-xl flex items-center gap-2 shadow-md">
                <Clock className={`text-amber-500 dark:text-amber-400 ${isTimerRunning ? "animate-spin" : ""}`} size={14} />
                <span className="text-xs font-mono font-extrabold text-slate-800 dark:text-white">
                  {formatTime(matchSeconds)}
                </span>
              </div>
            )}

            {/* BOTÃO DE BAIXAR APP / PLAY STORE */}
            <button
              type="button"
              onClick={() => setShowDownloadAppModal(true)}
              className="bg-amber-400 hover:bg-amber-300 text-emerald-950 font-black text-xs px-3 py-1.5 rounded-xl transition cursor-pointer shadow-md flex items-center gap-1.5"
              title="Instalar aplicativo PWA no celular"
            >
              <Download size={14} />
              <span className="hidden md:inline">Baixar App</span>
            </button>

            {/* Ferramenta de Zoom (Escala da Tela) */}
            <div
              className={`hidden sm:flex items-center gap-1 p-1 rounded-xl border ${
                theme === "dark"
                  ? "bg-emerald-950/60 border-emerald-900/50 text-emerald-200"
                  : "bg-slate-100 border-slate-200 text-slate-700"
              }`}
            >
              <button
                type="button"
                onClick={handleZoomOut}
                disabled={zoomLevel <= 75}
                className="p-1 rounded-lg hover:bg-black/5 dark:hover:bg-emerald-800/40 disabled:opacity-30 transition cursor-pointer"
                title="Diminuir Zoom"
              >
                <ZoomOut size={15} />
              </button>
              <button
                type="button"
                onClick={handleResetZoom}
                className="text-[10px] font-mono font-extrabold px-1 py-0.5 hover:text-amber-500 dark:hover:text-amber-400 transition cursor-pointer select-none"
                title="Restaurar Zoom (100%)"
              >
                {zoomLevel}%
              </button>
              <button
                type="button"
                onClick={handleZoomIn}
                disabled={zoomLevel >= 140}
                className="p-1 rounded-lg hover:bg-black/5 dark:hover:bg-emerald-800/40 disabled:opacity-30 transition cursor-pointer"
                title="Aumentar Zoom"
              >
                <ZoomIn size={15} />
              </button>
            </div>

            {/* Central de Notificações */}
            <NotificationCenter
              userProfile={userProfile}
              onNavigate={(path) => {
                const targetTab = path.replace(/^\//, "");
                if (targetTab) setActiveTab(targetTab);
              }}
            />

            {/* Seletor de Tema (Claro / Escuro) */}
            <ThemeSelector
              currentTheme={theme}
              onThemeChange={setTheme}
              variant="compact"
            />
          </div>
        </>
      )}
    </header>
  );
};
